import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  Clock,
  CreditCard,
  DollarSign,
  MapPin,
  Receipt,
  Loader2,
  AlertCircle,
  Copy,
  Share2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPage() {
  const { sessionId, splitId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPaymentInfo();
  }, [sessionId, splitId]);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/pay/${sessionId}/${splitId}`);
      setPaymentInfo(response.data);
    } catch (err) {
      console.error('Error fetching payment info:', err);
      setError(err.response?.data?.detail || 'No se pudo cargar la información de pago');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Link copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Pago - ${paymentInfo?.business_name}`,
          text: `Paga tu parte de la cuenta: $${paymentInfo?.split?.amount?.toLocaleString()}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando información de pago...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Link no válido</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { business_name, business_address, unit_name, split, total_session, is_paid } = paymentInfo || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 flex items-center justify-center">
      <Card className="max-w-md w-full shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Receipt className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl">{business_name}</CardTitle>
          {business_address && (
            <CardDescription className="flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" />
              {business_address}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Unit/Table Info */}
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Mesa</p>
            <p className="text-lg font-bold">{unit_name}</p>
          </div>

          {/* Person Info */}
          <div className="text-center">
            <Badge variant="outline" className="text-base px-4 py-2">
              {split?.person_name || 'Tu parte'}
            </Badge>
          </div>

          {/* Amount */}
          <div className={`text-center p-6 rounded-xl ${
            is_paid 
              ? 'bg-green-50 border-2 border-green-200' 
              : 'bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200'
          }`}>
            {is_paid ? (
              <>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-600 font-semibold mb-2">¡Pagado!</p>
                <p className="text-3xl font-bold text-green-700">
                  ${split?.paid_amount?.toLocaleString() || split?.amount?.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {new Date(split?.paid_at).toLocaleString('es-AR')}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-2">Monto a pagar</p>
                <p className="text-4xl font-bold text-emerald-600">
                  ${split?.amount?.toLocaleString()}
                </p>
                {split?.percentage && (
                  <p className="text-sm text-muted-foreground mt-1">
                    ({split.percentage}% del total)
                  </p>
                )}
              </>
            )}
          </div>

          {/* Items if by_items split */}
          {split?.items?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-muted-foreground">Tus consumos:</p>
              <div className="space-y-1 bg-muted/50 rounded-lg p-3">
                {split.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.product_name}</span>
                    <span className="font-mono">${item.subtotal?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total session reference */}
          <div className="flex justify-between items-center text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
            <span>Total de la cuenta</span>
            <span className="font-mono">${total_session?.toLocaleString()}</span>
          </div>

          {/* Status */}
          {!is_paid && (
            <div className="flex items-center justify-center gap-2 text-amber-600">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Pendiente de pago</span>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col gap-3">
          {!is_paid && (
            <>
              {/* Show short code for cashier reference */}
              <div className="w-full p-3 bg-muted/50 rounded-lg text-center">
                <p className="text-xs text-muted-foreground mb-1">Código de pago</p>
                <p className="font-mono text-xl font-bold tracking-wider">
                  {splitId?.toUpperCase().slice(-6)}
                </p>
              </div>
              <p className="text-xs text-muted-foreground text-center w-full">
                Muestra este código al cajero para pagar tu parte
              </p>
              <div className="flex gap-2 w-full">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={copyLink}
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  Copiar link
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={shareLink}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartir
                </Button>
              </div>
            </>
          )}
          
          {is_paid && (
            <p className="text-xs text-muted-foreground text-center">
              Gracias por tu visita
            </p>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
